import {
  Box,
  Button,
  Callout,
  Flex,
  Grid,
  Separator,
  Switch,
  Text,
  TextField,
  TextArea,
  Heading,
} from "@radix-ui/themes"
import { AlertCircle } from "lucide-react"
import { SubmitEvent } from "react"
import ColorField from "@/shared/primitives/ColorField"
import { DEFAULT_ACCENT } from "@/shared/galleryTheme"

export interface ClubFormData {
  club: {
    name: string
    slug: string
    description: string
    history: string
    instagram: string
    threads: string
    facebook: string
    website: string
    locale: string
    galleries_enabled: boolean
    /** Whether this club may publish a blog. Nothing is billed for one, so
     *  unlike galleries this switch carries no quota behind it. */
    blogs_enabled: boolean
    /** Stored in bytes to match `byte_size`; the form edits it in GB. */
    storage_quota_bytes: number
    /** `#RRGGBB`, or "" for none. The house colour this club's galleries use
     *  when a manager hasn't picked one for the event. */
    gallery_accent: string
    /** The far end of the house gradient, or "" for a flat accent. Only means
     *  anything alongside `gallery_accent`, and is refused without it. */
    gallery_accent_2: string
  }
}

const GIGABYTE = 1024 ** 3

interface ClubFormProps {
  data: ClubFormData
  setData: <K extends keyof ClubFormData>(key: K, value: ClubFormData[K]) => void
  onSubmit: (e: SubmitEvent) => void
  processing: boolean
  errors?: { [key: string]: string[] }
  title: string
  submitLabel: string
}

export default function ClubForm({
  data,
  setData,
  onSubmit,
  processing,
  errors,
  title,
  submitLabel,
}: ClubFormProps) {
  const setClub = (fields: Partial<ClubFormData["club"]>) => {
    setData("club", { ...data.club, ...fields })
  }

  const errorMessages = errors ? Object.values(errors).flat() : []

  return (
    <Box className="border-2 py-8 px-10">
      <Heading size="7">{title}</Heading>
      <Text size="4" as="p" color="gray">
        Fill in the details below to create or edit a club.
      </Text>
      <Separator my="3" size="4" />

      {errorMessages.length > 0 && (
        <Callout.Root color="red" role="alert" mb="4">
          {errorMessages.map((error, index) => (
            <div key={index}>
              <Callout.Icon>
                <AlertCircle />
              </Callout.Icon>
              <Callout.Text>{error}</Callout.Text>
            </div>
          ))}
        </Callout.Root>
      )}

      <form onSubmit={onSubmit}>
        {/* Basic Info */}
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Club Name *
              </Text>
              <TextField.Root
                size="3"
                value={data.club.name}
                onChange={(e) => setClub({ name: e.target.value })}
                required
                placeholder="e.g. The Fast Drivers Club"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Slug *
              </Text>
              <TextField.Root
                size="3"
                value={data.club.slug}
                onChange={(e) => setClub({ slug: e.target.value })}
                required
                placeholder="e.g. fast-drivers-club"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Locale
              </Text>
              <TextField.Root
                size="3"
                value={data.club.locale}
                onChange={(e) => setClub({ locale: e.target.value })}
                placeholder="e.g. en"
              />
            </Flex>
          </Box>
        </Grid>

        {/* Content */}
        <Text size="5" weight="bold" mb="4">
          Content
        </Text>
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Description
              </Text>
              <TextArea
                size="3"
                value={data.club.description}
                onChange={(e) => setClub({ description: e.target.value })}
                placeholder="Club description..."
                style={{ minHeight: "120px" }}
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                History
              </Text>
              <TextArea
                size="3"
                value={data.club.history}
                onChange={(e) => setClub({ history: e.target.value })}
                placeholder="Club history..."
                style={{ minHeight: "120px" }}
              />
            </Flex>
          </Box>
        </Grid>

        {/* Social Media */}
        <Text size="5" weight="bold" mb="4">
          Social Media
        </Text>
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Website
              </Text>
              <TextField.Root
                size="3"
                type="url"
                value={data.club.website}
                onChange={(e) => setClub({ website: e.target.value })}
                placeholder="https://example.com"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Instagram
              </Text>
              <TextField.Root
                size="3"
                value={data.club.instagram}
                onChange={(e) => setClub({ instagram: e.target.value })}
                placeholder="e.g. @clubname"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Threads
              </Text>
              <TextField.Root
                size="3"
                value={data.club.threads}
                onChange={(e) => setClub({ threads: e.target.value })}
                placeholder="e.g. @clubname"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Facebook
              </Text>
              <TextField.Root
                size="3"
                value={data.club.facebook}
                onChange={(e) => setClub({ facebook: e.target.value })}
                placeholder="e.g. @clubname"
              />
            </Flex>
          </Box>
        </Grid>

        {/* Permissions */}
        <Text size="5" weight="bold" mb="4">
          Permissions
        </Text>
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text as="label" size="3" weight="medium">
                <Flex gap="2" align="center">
                  <Switch
                    checked={data.club.galleries_enabled}
                    onCheckedChange={(checked) =>
                      setClub({ galleries_enabled: checked })
                    }
                  />
                  Galleries enabled
                </Flex>
              </Text>
              <Text size="2" color="gray">
                Lets this club&rsquo;s events host photo and music galleries. Storage
                is billed to the club.
              </Text>
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text as="label" size="3" weight="medium">
                <Flex gap="2" align="center">
                  <Switch
                    checked={data.club.blogs_enabled}
                    onCheckedChange={(checked) =>
                      setClub({ blogs_enabled: checked })
                    }
                  />
                  Blogs enabled
                </Flex>
              </Text>
              <Text size="2" color="gray">
                Gives this club&rsquo;s managers a Blog tab to write and publish
                posts from. Nothing is stored or billed &mdash; posts are text.
              </Text>
            </Flex>
          </Box>

          {/* Only meaningful once galleries are on, so it follows the switch
              rather than sitting in its own section. */}
          {data.club.galleries_enabled && (
            <Box>
              <Flex gap="2" direction="column">
                <Text as="label" size="3" weight="medium">
                  Storage quota (GB)
                </Text>
                <TextField.Root
                  size="3"
                  type="number"
                  min="1"
                  style={{ maxWidth: 160 }}
                  value={Math.round(data.club.storage_quota_bytes / GIGABYTE)}
                  onChange={(e) =>
                    setClub({
                      storage_quota_bytes: Math.max(0, Number(e.target.value)) * GIGABYTE,
                    })
                  }
                />
                <Text size="2" color="gray">
                  Uploads that would take the club past this are refused. Counted
                  across every gallery on the club&rsquo;s events.
                </Text>
              </Flex>
            </Box>
          )}

          {data.club.galleries_enabled && (
            <Box>
              <Flex gap="2" direction="column">
                <Text as="label" size="3" weight="medium">
                  Gallery accent colour
                </Text>
                <ColorField
                  size="3"
                  label="Pick the club's house colour"
                  value={data.club.gallery_accent}
                  placeholder={DEFAULT_ACCENT}
                  onChange={(value) => setClub({ gallery_accent: value })}
                  // Clearing the house colour clears the pair: with nothing to
                  // blend from, a second colour on its own is refused.
                  onClear={() =>
                    setClub({ gallery_accent: "", gallery_accent_2: "" })
                  }
                />
                <Text size="2" color="gray">
                  The default for this club&rsquo;s galleries. Club managers can
                  override it per event. Left blank, galleries use Final Bout
                  red. Colours too dark to read on the gallery&rsquo;s near-black
                  background are refused.
                </Text>
              </Flex>
            </Box>
          )}

          {data.club.galleries_enabled && (
            <Box>
              <Flex gap="2" direction="column">
                <Text as="label" size="3" weight="medium">
                  Gallery second colour
                </Text>
                <ColorField
                  size="3"
                  label="Pick the club's second house colour"
                  value={data.club.gallery_accent_2}
                  placeholder=""
                  fallback={data.club.gallery_accent || DEFAULT_ACCENT}
                  onChange={(value) => setClub({ gallery_accent_2: value })}
                  onClear={() => setClub({ gallery_accent_2: "" })}
                />
                <Text size="2" color="gray">
                  Optional. Set it and the club&rsquo;s galleries blend from the
                  accent on the left to this on the right across every filled
                  surface. Needs an accent colour above to blend from; a manager
                  overriding the accent for their event takes over both.
                </Text>
              </Flex>
            </Box>
          )}
        </Grid>

        {/* Submit */}
        <Flex gap="3" justify="end" pt="4">
          <Button size="3" type="submit" disabled={processing}>
            {submitLabel}
          </Button>
        </Flex>
      </form>
    </Box>
  )
}
